<script setup lang="ts">
interface Props {
  matrix: Record<string, Record<string, number>>
  grandTotal: number
}

const props = defineProps<Props>()

// Journey stages configuration
const journeyStages = [
  { key: 'Rookie', label: 'Rookie', description: 'New customers' },
  { key: 'Occasional', label: 'Occasional', description: 'Infrequent buyers' },
  { key: 'Loyalist', label: 'Loyalist', description: 'Frequent buyers' },
  { key: 'Whale', label: 'Whale', description: 'High spenders' }
]

// Attrition risks
const attritionRisks = ['Healthy', 'Sliding', 'Churned']

// Get cell background opacity based on count
const getCellOpacity = (count: number) => {
  if (!props.matrix || count === 0) return 0

  // Find max count in matrix
  const allCounts: number[] = []
  Object.values(props.matrix).forEach((row: any) => {
    Object.values(row).forEach((val: any) => {
      if (typeof val === 'number') allCounts.push(val)
    })
  })

  const maxCount = Math.max(...allCounts)
  if (maxCount === 0) return 0

  // Scale opacity from 0.05 to 0.6
  const minOpacity = 0.05
  const maxOpacity = 0.6
  return minOpacity + ((count / maxCount) * (maxOpacity - minOpacity))
}
</script>

<template>
  <div class="overflow-x-auto">
    <table class="w-full border-collapse table-fixed">
      <colgroup>
        <col style="width: 25%" />
        <col style="width: 25%" />
        <col style="width: 25%" />
        <col style="width: 25%" />
      </colgroup>
      <thead>
        <tr class="border-b border-gray-200 dark:border-gray-700">
          <th class="text-left p-3 text-sm font-semibold text-gray-700 dark:text-gray-300">Journey Stage</th>
          <th class="text-center p-3">
            <div class="text-sm font-semibold text-green-600 dark:text-green-400">Healthy</div>
            <div class="text-xs font-normal text-gray-500 dark:text-gray-400">Recently visited</div>
          </th>
          <th class="text-center p-3">
            <div class="text-sm font-semibold text-orange-600 dark:text-orange-400">Sliding</div>
            <div class="text-xs font-normal text-gray-500 dark:text-gray-400">Away for a while</div>
          </th>
          <th class="text-center p-3">
            <div class="text-sm font-semibold text-red-600 dark:text-red-400">Churned</div>
            <div class="text-xs font-normal text-gray-500 dark:text-gray-400">Inactive for months</div>
          </th>
        </tr>
      </thead>
      <tbody v-if="matrix">
        <tr
          v-for="stage in journeyStages"
          :key="stage.key"
          class="border-b border-gray-100 dark:border-gray-800"
        >
          <td class="p-3">
            <div class="font-semibold text-sm text-gray-700 dark:text-gray-300">{{ stage.label }}</div>
            <div class="text-xs text-gray-500 dark:text-gray-400">{{ stage.description }}</div>
          </td>
          <CustomerSegmentMatrixCell
            v-for="risk in attritionRisks"
            :key="`${stage.key}-${risk}`"
            :count="matrix[stage.key]?.[risk] || 0"
            :journey-stage="stage.key"
            :attrition-risk="risk"
            :opacity="getCellOpacity(matrix[stage.key]?.[risk] || 0)"
            :grand-total="grandTotal"
          />
        </tr>
      </tbody>
      <tbody v-else>
        <tr>
          <td colspan="4" class="text-center py-8 text-gray-500 dark:text-gray-400">
            Loading matrix...
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
